// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { forwardRef, useId } from 'react';
import { InputLabel } from './InputLabel';
import './TextInput.css';

interface TextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  fluid?: boolean;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      error,
      helperText,
      iconLeft,
      iconRight,
      required,
      disabled,
      fluid = false,
      className = '',
      value,
      id: idProp,
      ...rest
    },
    ref,
  ) => {
    const autoId = useId();
    const id = idProp ?? autoId;
    const isFilled = value !== undefined && value !== '';

    const wrapperClasses = [
      'fin-input',
      error && 'fin-input--error',
      disabled && 'fin-input--disabled',
      isFilled && 'fin-input--filled',
      fluid && 'fin-input--fluid',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        {label && (
          <InputLabel htmlFor={id} required={required}>
            {label}
          </InputLabel>
        )}
        <div className="fin-input__wrapper">
          {iconLeft && (
            <div className="fin-input__icon fin-input__icon--left">
              {iconLeft}
            </div>
          )}
          <input
            ref={ref}
            id={id}
            className="fin-input__field"
            disabled={disabled}
            required={required}
            value={value}
            aria-invalid={!!error}
            aria-describedby={
              error ? `${id}-error` : helperText ? `${id}-helper` : undefined
            }
            {...rest}
          />
          {iconRight && (
            <div className="fin-input__icon fin-input__icon--right">
              {iconRight}
            </div>
          )}
        </div>
        {error && (
          <div className="fin-input__error" id={`${id}-error`} role="alert">
            {error}
          </div>
        )}
        {!error && helperText && (
          <div className="fin-input__helper" id={`${id}-helper`}>
            {helperText}
          </div>
        )}
      </div>
    );
  },
);

TextInput.displayName = 'TextInput';
